// ── CovenantCalendarForm ──
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { fmtDate } from "../../../shared/format";
import { Btn, FInput, FSelect, Field, Modal, ReadOnlyForm } from "../../../shared/ui";
import { useFreshForm } from "../../../shared/hooks";
import { ExceptionsPanel } from "../components/ExceptionsPanel";
import { NotesPanel } from "../components/NotesPanel";
import { useApp } from "../../../shared/appContext";

export const CovenantCalendarForm = ({ initial, covenantid, readOnly, onClose, onSaved, onRequestEdit }) => {
    const { api, role, modal, setModal, closeModal, saving, error, saveRec, app, setConfirm } = useApp();
      const [f, set] = useFreshForm(initial || {
        covenantid: covenantid||"", portfoliomanager:"", status:"Not Started",
        startdate:"", testdate:"", duedate:"", completeddate:"", covenantvalue:"", boxurl:"", description:""
      }, "CovenantCalendar");
      const [pms, setPms] = useState([]);
      const [localSaving, setLocalSaving] = useState(false);
      const [localErr, setLocalErr] = useState(null);
      useEffect(()=>{ api.get("getUsers?filter=isportfoliomanager").then(setPms).catch(()=>setPms([])); },[]);
      const nested = !!onClose;
      const close = onClose || closeModal;
      const pmOptions = [{ value:"", label:"— Select —" }, ...pms.map(u=>({ value:u.id, label:u.FullName }))];
      const statusOptions = ["Not Started","In-Progress","Completed","Canceled","On Hold","Waived"];
      // Same two-part test the server uses (shared/notify.js) — category AND
      // name — so the warning appears on exactly the entries that will send a
      // notice, and on no others. Trimmed and case-insensitive because both
      // values are typed by hand.
      const norm = s => String(s ?? "").trim().toLowerCase();
      const isFieldExam = norm(initial?.covenantname) === "field exam";
      const sendsNotice = isFieldExam && norm(initial?.covenantcategory) === "3rd party exam";

      // How many exceptions this entry has, for the warning below. Only fetched
      // for the entries that can trigger a notice, so no other form pays for it.
      const [excCount, setExcCount] = useState(null);   // null = not known yet
      useEffect(() => {
        if (!initial?.id || !sendsNotice) { setExcCount(null); return; }
        let alive = true;
        api.get("getCovenantExceptions?covenantcalendarid=" + initial.id)
          // A non-array answer means the lookup did not work, NOT that there
          // are zero exceptions. Reading it as zero would pop the warning on an
          // exam that has three of them written up — and a dialog that cries
          // wolf is one people learn to click through without reading.
          .then(r => { if (alive) setExcCount(Array.isArray(r) ? r.length : null); })
          .catch(() => { if (alive) setExcCount(null); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [initial?.id, sendsNotice]);
      const ST = ({ title }) => <div style={{ fontSize:11, fontWeight:600, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", marginTop:18, marginBottom:8, paddingBottom:4, borderBottom:"1.5px solid "+B.cyanLight, gridColumn:"1/-1" }}>{title}</div>;
      const RO = ({ label, value }) => <Field label={label}><div style={{ padding:"7px 10px", fontSize:13, borderRadius:4, border:"1px solid #ddd", background:"#f5f5f5", color:"#555", minHeight:18 }}>{value||"—"}</div></Field>;
      const TA = { width:"100%", boxSizing:"border-box", padding:"7px 10px", fontSize:13, borderRadius:4, border:"1px solid #999", background:"#fff", color:"#1a1a1a", fontFamily:"inherit" };
      const doSave = async () => {
            const body = { ...f, covenantid: f.covenantid || covenantid };
            if (body.completeddate) body.status = 'Completed';
            if (nested) {
              setLocalSaving(true); setLocalErr(null);
              try { await api.post("saveCovenantCalendar", body); onSaved && onSaved(); close(); }
              catch (e) { setLocalErr(e.message || "Save failed"); }
              setLocalSaving(false);
            } else {
              saveRec("saveCovenantCalendar", body, initial?"Edited":"Added", "CovenantCalendar",
                (initial?"Updated":"Added")+" covenant calendar entry", modal.onDone);
            }
          };

      // ── Completing a field exam sends an email, right now ──────────────────
      //
      // The notice goes out on completion with the exceptions that exist at
      // that instant, and its body is frozen once queued — so an exception
      // typed thirty seconds later never reaches the recipients. Rather than
      // add a second step to the process for every exam, the warning appears
      // only when there is something to warn about: a field exam being
      // completed with no exceptions written up.
      //
      // Conditions, all of them:
      //   - this entry is one that sends a notice (Field Exam / 3rd Party Exam)
      //   - it is going from not-completed to Completed by this save
      //   - the exception count came back and it is zero
      //
      // excCount === null means the lookup has not finished or failed. NOT a
      // reason to warn: a spurious "your exceptions are missing" on an exam
      // that has three of them teaches people to click through the dialog, and
      // then it protects nothing.
      const wasCompleted = norm(initial?.status) === "completed";
      const nowCompleted = norm(f.status) === "completed" || !!f.completeddate;
      const willNotify   = sendsNotice && nowCompleted && !wasCompleted;

      const saveEntry = () => {
            if (willNotify && excCount === 0) {
              setConfirm({
                // Confirm defaults to "Confirm delete" and a red button. Both
                // would be wrong and alarming here.
                title: "No exceptions recorded",
                variant: "primary",
                confirmLabel: "Complete and send",
                msg: "You're about to complete this Field Exam. A notification goes out to the "
                   + "portfolio managers as soon as you save, and this exam has no exceptions "
                   + "recorded yet — anything you add afterwards will be missing from that email. "
                   + "Add the exceptions and PM responses first, or continue if there really are none.",
                // Confirm does not dismiss itself, so this has to.
                onConfirm: () => { setConfirm(null); doSave(); },
              });
              return;
            }
            doSave();
          };
    return <Modal title={readOnly ? `View Calendar Entry (${initial?.id ?? ""})` : initial ? "Edit Calendar Entry" : "Add Calendar Entry"} onClose={close} wide
      onEdit={readOnly && role.canEdit ? (nested ? onRequestEdit : ()=>{ closeModal(); setModal({type:"covenantcalendar-edit",rec:initial,onDone:modal.onDone}); }) : null}>
        <ReadOnlyForm readOnly={readOnly}>
          {initial && <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:"0 16px", marginBottom:4 }}>
            <RO label="Account" value={initial.companyname} />
            <RO label="Covenant" value={initial.covenantname} />
            <RO label="Category" value={initial.covenantcategory} />
            <RO label="Frequency" value={initial.covenantfrequency} />
            <RO label="Direction" value={initial.covenantdirection} />
            <RO label="Value Guidance" value={initial.valueguidance} />
            <div style={{ gridColumn:"span 2" }}><RO label="Requirement" value={initial.covenantrequirement} /></div>
          </div>}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:"0 16px" }}>
            <ST title="Tracking" />
            <Field label="Status"><FSelect value={f.status||""} onChange={v=>{ set("status")(v); set("completeddate")(v==="Completed" ? new Date().toISOString().slice(0,10) : ""); }} options={statusOptions} /></Field>
            <Field label="Completed By"><FSelect value={f.completedby||""} onChange={set("completedby")} options={pmOptions} /></Field>
            <Field label="Reported Value"><FInput value={f.covenantvalue||""} onChange={set("covenantvalue")} /></Field>
            <div />
            <Field label="Test Date"><FInput type="date" value={f.testdate?.slice(0,10)||""} onChange={set("testdate")} /></Field>
            <Field label="Due Date"><FInput type="date" value={f.duedate?.slice(0,10)||""} onChange={set("duedate")} /></Field>
            <RO label="Completed Date" value={f.completeddate ? fmtDate(f.completeddate) : "—"} />
            <div style={{ gridColumn:"1/-1" }}><Field label="Box URL">{readOnly
              ? <div style={{ padding:"7px 10px", fontSize:13, borderRadius:4, border:"1px solid #ddd", background:"#f5f5f5", minHeight:18 }}>
                  {f.boxurl
                    ? <a href={f.boxurl} target="_blank" rel="noopener noreferrer" style={{ color:B.cyanDark, textDecoration:"underline", wordBreak:"break-all" }}>{f.boxurl}</a>
                    : "—"}
                </div>
              : <FInput value={f.boxurl||""} onChange={set("boxurl")} placeholder="https://app.box.com/..." />}</Field></div>            <div style={{ gridColumn:"1/-1" }}><Field label="Description"><textarea value={f.description||""} onChange={e=>set("description")(e.target.value)} rows={2} style={TA} /></Field></div>
          </div>
        </ReadOnlyForm>
 
        {/* ===== Field Exam Exceptions (between Description and Notes) ===== */}
        {initial?.id && isFieldExam && <ExceptionsPanel entry={initial} />}
 
        {initial?.id && <NotesPanel scope="record" entitytype="CovenantCalendar" entityid={initial.id} accountid={initial.accountid} source="Covenant Calendar" />}
        {(nested ? localErr : (!readOnly && error)) && <div style={{ color:"#993C1D",fontSize:12,marginBottom:8,marginTop:8 }}>{nested ? localErr : error}</div>}
              <div style={{ display:"flex",gap:8,justifyContent:"flex-end",marginTop:16 }}>
                <Btn onClick={close}>{readOnly?"Close":"Cancel"}</Btn>
                {!readOnly && <Btn disabled={nested?localSaving:saving} onClick={saveEntry} variant="primary">{(nested?localSaving:saving)?"Saving...":"Save"}</Btn>}
              </div>
            </Modal>;
            };
